import React from 'react';
import type { BarberGrowthSnapshot } from '../../services/barberDataService';

/**
 * Piece 3 — Growth Track goals. The Growth Score components ARE the barber's auto-tracked goals
 * (each component is 0-100 where 100 = at target). Shows progress bars + the stage + occupancy goal.
 * Reads the persisted snapshot. Null-safe. Goals tab of the profile (replaces the old stub for growth shops).
 * BEHAVIOR-PIN: SLIK-GROWTH-SYSTEM-BLUEPRINT.md · "Growth system IS the goals system" (Jose 2026-08-22).
 */
const GOAL_META: Record<string, { label: string; goal: string }> = {
  occupancy: { label: 'Occupancy (fill your book)', goal: 'stage target' },
  rebooking: { label: 'Rebooking (book the next cut)', goal: '50%' },
  returning: { label: 'Returning clients (loyalty)', goal: '70%' },
  retail: { label: 'Retail (product per service)', goal: '5% of services' },
  rating: { label: 'Rating', goal: '4.9' },
};
const ORDER = ['occupancy', 'rebooking', 'returning', 'retail', 'rating'] as const;

export const GrowthTrackGoals: React.FC<{ growth?: BarberGrowthSnapshot | null }> = ({ growth }) => {
  if (!growth) return null;
  const comps = growth.components || ({} as any);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
        <h3 className="font-bold text-lg text-gray-900 dark:text-white">🎯 Growth Track goals</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">Stage {growth.stage} · {growth.stageName}</span>
      </div>
      <div className="space-y-3">
        {ORDER.map((k) => {
          const score = Math.max(0, Math.min(100, comps[k] ?? 0));
          const meta = GOAL_META[k];
          const goalText = k === 'occupancy' ? `${growth.occupancyTarget}%` : meta.goal;
          const done = score >= 100;
          // Rating neutralizes to 0 until enough reviews exist; show that honestly rather than "0%".
          // Fills in once the Fresha review pull lands (Jose 2026-09-27).
          const awaiting = k === 'rating' && score === 0;
          return (
            <div key={k}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-800 dark:text-gray-200">{meta.label} <span className="text-xs text-gray-400">→ {goalText}</span></span>
                <span className={`font-semibold ${awaiting ? 'text-gray-400 italic' : done ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'}`}>{awaiting ? 'awaiting reviews' : done ? '✓ met' : `${score}%`}</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                {!awaiting && <div className={`h-full rounded-full ${done ? 'bg-green-500' : 'bg-purple-500'}`} style={{ width: `${score}%` }} />}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-400 mt-3">Each bar is progress toward that goal (100% = target hit). Targets are shop settings.</p>
    </div>
  );
};

export default GrowthTrackGoals;
